import { addDays } from 'date-fns/addDays';
import { param } from 'saddlebag-url';

import { formatDateNonlocalised } from '../../../../bootstrap/localisation';
import { INS_MONTH } from '../constants/urlConstants';
import { normaliseMoneyAmount } from '../presentation/normaliseMoneyAmount';

import hotelsUrlBuilder from './hotelsUrlBuilder';

import type { StayState } from '../../selectors/types';
import type {
  SkyscannerCommonV1Money,
  SkyscannerMichelinCommonCarV1Caroptions,
} from '@skyscanner-internal/michelin-web-http-client';

const CAR_HIRE_DEFAULT_PICKUP_TIME = '10:00';
const CAR_HIRE_DEFAULT_DRIVER_AGE = '30';

const formatCarHireDateTime = (date: Date): string =>
  `${formatDateNonlocalised(date)}T${CAR_HIRE_DEFAULT_PICKUP_TIME}`;

export const buildAirlineLogoUrl = (airlineCode: string | undefined) => {
  if (!airlineCode) {
    return undefined;
  }

  return `https://www.skyscanner.net/images/airlines/small/${encodeURIComponent(
    airlineCode,
  )}.png`;
};

export const buildFlightRedirectUrl = ({
  baseUrl,
  price,
}: {
  baseUrl: string;
  price: SkyscannerCommonV1Money.Money | undefined;
}): string => {
  const amount = normaliseMoneyAmount(price);
  if (amount == null || !price?.currencyCode) return baseUrl;

  const priceParams = param(
    {
      qp_prevProvider: INS_MONTH,
      qp_prevCurrency: price.currencyCode,
      qp_prevPrice: amount,
    },
    true,
  );

  return `${baseUrl}&${priceParams}`;
};

export const buildHotelRedirectUrl = ({
  destinationId,
  hotelId,
  impressionId,
  inboundDate,
  outboundDate,
  price,
  sortByPriceAsc,
  stay,
}: {
  destinationId: string | undefined;
  hotelId?: string;
  impressionId: string;
  inboundDate: Date | null;
  outboundDate: Date;
  price?: SkyscannerCommonV1Money.Money;
  sortByPriceAsc?: boolean;
  stay: StayState;
}): string | undefined => {
  if (!destinationId) return undefined;

  return hotelsUrlBuilder.buildDayviewUrl({
    checkinDate: outboundDate,
    checkoutDate: inboundDate ?? addDays(outboundDate, 1),
    skyId: destinationId,
    stay,
    impressionId,
    hotelId,
    price,
    sortByPriceAsc,
  });
};

const carTypeToFilterSlug = (
  carType: SkyscannerMichelinCommonCarV1Caroptions.Type | undefined,
): string | null => {
  if (!carType || carType === 'TYPE_UNSPECIFIED') return null;
  return carType.replace(/^TYPE_/, '').toLowerCase();
};

const CAR_HIRE_COMBO_SOURCE = 'combined_result';

export const buildCarHireRedirectUrl = ({
  carType,
  destinationEntityId,
  inboundDate,
  outboundDate,
  price,
}: {
  carType?: SkyscannerMichelinCommonCarV1Caroptions.Type;
  destinationEntityId: string | undefined;
  inboundDate: Date | null;
  outboundDate: Date;
  price?: SkyscannerCommonV1Money.Money;
}): string => {
  if (!destinationEntityId) return '/carhire/';

  const dropoff = inboundDate ?? addDays(outboundDate, 1);
  const baseUrl = `/carhire/results/${destinationEntityId}/${destinationEntityId}/${formatCarHireDateTime(outboundDate)}/${formatCarHireDateTime(dropoff)}/${CAR_HIRE_DEFAULT_DRIVER_AGE}`;

  const queryParams: Record<string, string> = { source: CAR_HIRE_COMBO_SOURCE };
  const filterSlug = carTypeToFilterSlug(carType);
  if (filterSlug) queryParams.filters = `car-type:${filterSlug}`;
  const amount = normaliseMoneyAmount(price);
  if (amount != null) queryParams.indicative_price = String(amount);

  const queryString = Object.keys(queryParams).length
    ? `?${param(queryParams, true)}`
    : '';

  return `${baseUrl}${queryString}`;
};
